import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { SessionManager } from '@services/session-manager';
import { ApiBaseService } from './api-base.service';

import { RegisterModel, RegisterAccountModel, RegisterAddressModel, CardPaymentModel, RegisterPhoneModel, RegisterEmailModel } from '@models/RegisterModel';
import { CustomerModel, CustomerPhoneFindModel } from '@models/CustomerModel';
import { RecoverModel } from '@models/RecoverModel';
import { ChangeModel } from '@models/ChangeModel';
import { CustomerCardModel } from '@models/CardModel';
import { MarketCategory } from '@models/CompanyModel';
import { SharedService } from './shared.service';
import { CompanyCustomerModel } from '@models/CompanyCustomerModel';


@Injectable()
export class ApiAccountService extends ApiBaseService {

    constructor(public http: HttpClient, public sharedService: SharedService) {
        super(http, sharedService);
    }

    // GET api/admin/account/reads/v1/{id}
    getAccount(): Promise<CustomerModel> {

        const session = this.getSession();
        if (session && session.user) {

            this.refreshSession();

            const id = session.user.id;

            return this.http.get(`${this.baseUrl}/api/admin/account/reads/v1/${id}`, this.getHttpOptions())
                .toPromise().then(
                    (user: any) => {

                        const customer = new CustomerModel(user);

                        SessionManager.updateSessionAccount(customer);
                        this.sharedService.setAccount(customer);

                        return customer;
                    },
                    (err: HttpErrorResponse) => {

                        // Unauthorized
                        if (err.status == 401 && err.error) {

                            this.sharedService.showSessionTimeoutAlert(err.error);
                        }
                        // NotFound
                        else if (err.status == 404) {

                            this.sharedService.showSessionTimeoutAlert(
                                'Error al cargar información de la cuenta, debe iniciar sesión nuevamente');
                        }

                        const errMsg = this.getErrorMsg(err, null, true);

                        return Promise.reject(new Error(`Error al cargar información de la cuenta: ${errMsg}`));
                    }
                );
        }
        return Promise.reject(new Error(`Error al cargar información de la cuenta, debe iniciar session.`));
    }

    // POST api/admin/account/phone/reads/v1
    getAccountWithPhone(customer: CustomerPhoneFindModel): Promise<any>  {

        return this.http.post(`${this.baseUrl}/api/admin/account/phone/reads/v1`, customer, this.getHttpOptions())
            .toPromise();
    }

    // POST api/admin/account/phone/reads/v1
    getCheckoutAccountWithPhone(customer: CustomerPhoneFindModel, country): Promise<any>  {

        return this.http.post(`${this.baseUrl}/api/admin/account/phone/reads/v1`, customer, this.getHttpOptionsWithoutSession(country))
            .toPromise();
    }


    // PUT api/admin/account/update/v1
    updateAccount(account: CustomerModel): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/account/update/v1`, account, this.getHttpOptions())
            .toPromise();
    }

    // PUT api/admin/account/update/v1
    deleteAccount(userId: number): Promise<any> {

        return this.http.delete(`${this.baseUrl}/api/admin/account/delete/v1/${userId}`, this.getHttpOptions())
            .toPromise();
    }

    // PUT api/admin/account/notify/update/v1
    deleteAccountNotify(userId: number): Promise<any> {

        return this.http.delete(`${this.baseUrl}/api/admin/account/notify/delete/v1/${userId}`, this.getHttpOptions())
            .toPromise();
    }

    // GET api/admin/account/companies/reads/v1/{id}
    getAllCompanies(id: number): Promise<CompanyCustomerModel[]> {

        return this.http.get(`${this.baseUrl}/api/admin/account/companies/reads/v1/${id}`, this.getHttpOptions())
            .toPromise().then(
                (results: any) => {

                    const companies = results.map((result: any) => {

                        return new CompanyCustomerModel(result);
                    });

                    return companies;
                }
            );
    }

    // POST api/admin/account/company/create/v1
    addCompanyCustomer(userId: number, code: string): Promise<CompanyCustomerModel> {

        const newCompanyCustomer = {
            customerId: userId,
            code: code
        };

        return this.http.post(`${this.baseUrl}/api/admin/account/company/create/v1`, newCompanyCustomer, this.getHttpOptions())
            .toPromise().then(
                (result: any) => {

                    const companyCustomer = new CompanyCustomerModel(result);

                    return companyCustomer;
                }
            );
    }

    // POST api/admin/account/company/delete/v1
    deleteCompanyCustomer(companyId: number): Promise<any> {

        return this.http.delete(`${this.baseUrl}/api/admin/account/company/delete/v1/${companyId}`, this.getHttpOptions())
            .toPromise();
    }


    // GET api/admin/account/card/reads/v1/{id}
    getAllCards(userId: number): Promise<CustomerCardModel[]> {

        return this.http.get(`${this.baseUrl}/api/admin/account/cards/reads/v1/${userId}`, this.getHttpOptions())
            .toPromise().then(
                (results: any) => {

                    const cards: CustomerCardModel[] = results.map((result: any) => {

                        return new CustomerCardModel(result);
                    });

                    return cards;
                }
            );
    }

    // POST api/admin/changecard/create/v1
    changeCard(changeCard: CardPaymentModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/register/changecard/create/v1`, changeCard, this.getHttpOptions())
            .toPromise();
    }

    // POST api/admin/account/card/create/v1
    addCard(addCard: CardPaymentModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/card/create/v1`, addCard, this.getHttpOptions())
            .toPromise();
    }

    // PUT api/admin/account/card/verify/update/v1
    verifyCard(card: CustomerCardModel): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/account/card/verify/update/v1`, card, this.getHttpOptions())
            .toPromise();
    }

    // PUT api/admin/account/card/current/update/v1
    updateCurrentCard(card: CustomerCardModel): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/account/card/current/update/v1`, card, this.getHttpOptions())
            .toPromise();
    }

    // POST api/admin/account/card/delete/v1
    deleteCard(token: string): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/card/delete/v1/${token}`, null, this.getHttpOptions())
            .toPromise();
    }


    // POST api/admin/account/create/v1
    registerCustomer(register: RegisterModel): Promise<RegisterModel> {

        return this.http.post(`${this.baseUrl}/api/admin/account/create/v1`, register, this.getHttpOptions())
            .toPromise().then(
                (result: any) => {

                    return new RegisterModel(result);
                }
            );
    }

    // PUT api/admin/account/account/update/v1
    registerAccount(registerAccount: RegisterAccountModel): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/account/register/update/v1`, registerAccount, this.getHttpOptions())
            .toPromise();
    }

    //PUT api/admin/account/register/phone/update/v1
    registerPhone(registerPhone: RegisterPhoneModel): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/account/register/phone/update/v1`, registerPhone, this.getHttpOptions())
            .toPromise();
    }

    //PUT api/admin/account/register/email/update/v1
    registerEmail(registerEmail: RegisterEmailModel): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/account/register/email/update/v1`, registerEmail, this.getHttpOptions())
            .toPromise();
    }

    // PUT api/admin/account/account/update/v1
    registerAddress(registerAddress: RegisterAddressModel): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/account/address/update/v1`, registerAddress, this.getHttpOptions())
            .toPromise();
    }


    // POST api/admin/account/passwordrecovery/email/v1
    passwordRecoveryWithEmail(recover: RecoverModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/passwordrecovery/email/v1`, recover, this.getHttpOptions())
            .toPromise();

    }

    // POST api/admin/account/passwordrecovery/phone/v1
    passwordRecoveryWithPhone(recover: RecoverModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/passwordrecovery/phone/v1`, recover, this.getHttpOptions())
            .toPromise();

    }

    // POST api/admin/account/passwordchange/email/v1
    changePasswordWithEmail(change: ChangeModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/passwordchange/email/v1`, change, this.getHttpOptions())
            .toPromise();

    }

    // POST api/admin/account/passwordchange/phone/v1
    changePasswordWithPhone(change: ChangeModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/passwordchange/phone/v1`, change, this.getHttpOptions())
            .toPromise();

    }

    
    // POST api/admin/account/checkemail/v1
    verifyEmail(register: RegisterModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/checkemail/v1`, register, this.getHttpOptions())
            .toPromise();

    }

    /**
     * POST api/admin/account/checkaccount/v1
     * Manda el mismo código por correo y WhatsApp de una sola vez.
     * Responde { email: boolean, whatsApp: boolean } con lo que sí salió.
     */
    verifyAccount(register: RegisterModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/checkaccount/v1`, register, this.getHttpOptions())
            .toPromise();
    }

    // POST api/admin/account/checkphone/v1
    verifyPhone(register: RegisterModel): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/checkphone/v1`, register, this.getHttpOptions())
            .toPromise();

    }

    // POST api/admin/account/checkemail/v1
    checkInviteCode(code: string): Promise<any> {

        return this.http.post(`${this.baseUrl}/api/admin/account/checkinvite/v1/${code}`, null, this.getHttpOptions())
            .toPromise();

    }

    // PUT: api/admin/account/card-name/update/v1
    putCustomerCard(customerCard: CustomerCardModel): Promise<any> {

        return this.http.put(`${this.baseUrl}/api/admin/account/card-name/update/v1`, customerCard, this.getHttpOptions())
            .toPromise();
    }
}
